import {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Hidden,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  Business as BusinessIcon,
  ExitToApp as ExitToAppIcon,
  VpnLock as VpnLockIcon,
} from '@material-ui/icons';

import { hasPermissions } from 'utils/permissions';

import AccountModal from './AccountModal';

const useStyles = makeStyles((theme) => ({
  avatar: {
    height: 32,
    width: 32,
    marginRight: theme.spacing(1),
  },
  avatarArrow: {
    paddingLeft: '0.15em',
  },
  popover: {
    width: 220,
  },
  // Variante para el sidebar oscuro (§3.1)
  sidebarRoot: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    minWidth: 0,
  },
  sidebarButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(0.5),
    borderRadius: 9,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,.08)',
    },
  },
  sidebarAvatar: {
    height: 32,
    width: 32,
    flexShrink: 0,
    fontSize: 13,
    fontWeight: 700,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  sidebarTexts: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarName: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.common.white,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sidebarEmail: {
    fontSize: 11.5,
    lineHeight: 1.3,
    color: 'rgba(255,255,255,.5)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  sidebarLogout: {
    color: 'rgba(255,255,255,.6)',
    padding: 6,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,.10)',
      color: theme.palette.common.white,
    },
  },
}));

function AccountMenuControl({ variant, collapsed }) {
  const classes = useStyles();
  const history = useHistory();
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState();
  const [modal, setModal] = useState();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async function () {
    try {
      await Auth.signOut({
        global: true,
      });
    } catch (error) {
      console.debug(error.message);
    } finally {
      window.location.replace('/auth/login');
    }
  };

  const handleModalAccount = async function () {
    const user = await Auth.currentAuthenticatedUser();

    setModal({
      data: {
        frmSetPreferredMFA: {
          mfa: {
            SMS_MFA: 'SMS',
            SOFTWARE_TOKEN_MFA: 'TOTP',
          }[user?.preferredMFA] || 'NOMFA',
          phone_number: user?.attributes?.phone_number,
        },
        frmUserToken: user,
      },
      events: {
        onClose: () => setModal(),
      },
      open: true,
    });
  };

  const handleInit = async function () {
    const user = await Auth.currentAuthenticatedUser();

    const label = [
      user?.attributes?.name || '',
      user?.attributes?.family_name || '',
    ].filter((item) => !!item).join(' ')
      || user?.attributes?.email
      || user?.attributes?.phone_number
      || user?.username
      || 'Usuario';

    setCurrentUser({
      label,
      // Solo presentación: email e inicial para el bloque del sidebar
      email: user?.attributes?.email
        || user?.attributes?.phone_number
        || user?.username
        || '',
      initial: label.trim().charAt(0).toUpperCase() || 'U',
    });
  };

  const menuOptions = [
    {
      hidden: !hasPermissions('company.select.getCompany') || process.env.NODE_ENV === 'production',
      icon: <BusinessIcon fontSize="small" />,
      label: 'Compañia',
      fn: function () {
        history.push('/company');
      },
    },
    {
      icon: <VpnLockIcon fontSize="small" />,
      label: 'Cuenta',
      fn: handleModalAccount,
    },
    {
      type: 'divider',
    },
    {
      icon: <ExitToAppIcon fontSize="small" />,
      label: 'Cerrar sesión',
      fn: handleLogout,
    },
  ];

  useEffect(() => {
    handleInit();
  }, []);

  return (
    <>
      <AccountModal {...modal} />

      {variant === 'sidebar' ? (
        <Box className={classes.sidebarRoot}>
          <Tooltip
            title={collapsed ? (currentUser?.label || 'Cuenta') : ''}
            placement="right"
          >
            <ButtonBase
              className={classes.sidebarButton}
              onClick={handleOpen}
              ref={ref}
              style={collapsed ? {
                flex: 'none',
                justifyContent: 'center',
              } : undefined}
            >
              <Avatar
                alt="User"
                className={classes.sidebarAvatar}
              >
                {currentUser?.initial || 'U'}
              </Avatar>
              {!collapsed && (
                <Box className={classes.sidebarTexts}>
                  <Typography className={classes.sidebarName}>
                    {currentUser?.label || 'Cuenta'}
                  </Typography>
                  {currentUser?.email && (
                    <Typography className={classes.sidebarEmail}>
                      {currentUser.email}
                    </Typography>
                  )}
                </Box>
              )}
            </ButtonBase>
          </Tooltip>
          {!collapsed && (
            <Tooltip title="Cerrar sesión" placement="top">
              <IconButton
                className={classes.sidebarLogout}
                onClick={handleLogout}
                size="small"
              >
                <ExitToAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : (
        <Box
          display="flex"
          alignItems="center"
          component={ButtonBase}
          onClick={handleOpen}
          ref={ref}
        >
          <Avatar alt="User" className={classes.avatar} />
          {currentUser && (<Hidden smDown>
            <Typography
              children={currentUser?.label}
              color="inherit"
              variant="h6"
            />
            {isOpen
              ? <ArrowDropUpIcon className={classes.avatarArrow} />
              : <ArrowDropDownIcon className={classes.avatarArrow} />
            }
          </Hidden>)}
        </Box>
      )}
      <Menu
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        keepMounted
        PaperProps={{
          className: classes.popover,
        }}
        getContentAnchorEl={null}
        anchorEl={ref.current}
        open={isOpen}
      >
        {menuOptions.filter(item => item.hidden !== true).map((item, index) => {

          if (item.type === 'divider') {
            return (<Divider key={index} />);
          }

          return (<MenuItem
            children={<>
              <ListItemIcon children={item.icon} /> {item.label}
            </>}
            key={index}
            onClick={() => {
              handleClose();

              if (typeof item.fn === 'function') {
                item.fn();
              }
            }}
          />);
        })}
      </Menu>
    </>
  );
}

AccountMenuControl.defaultProps = {
  collapsed: false,
  variant: 'topbar',
};

AccountMenuControl.propTypes = {
  collapsed: PropTypes.bool,
  variant: PropTypes.oneOf([
    'sidebar',
    'topbar',
  ]),
};

export default AccountMenuControl;
